import { Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { IssueModule } from '../models/support.models';
import { moduleFromRoute } from './route-module.map';

/**
 * Remembers the last product module the user was actually looking at, so the standalone
 * Report page can prefill "Where did this happen?".
 *
 * The FAB opens the form as a modal ON the broken page, so it can read the module from the
 * current route. The Report PAGE cannot — by the time it renders, the route is
 * /app/support/report and the original context is gone. Without this, that field defaults
 * to "Not specified" and stays there, which loses the single most useful triage signal.
 *
 * Support routes are deliberately ignored: navigating Report → My Issues → Report must not
 * overwrite the module the user was on before they came here.
 */
@Injectable({ providedIn: 'root' })
export class LastModuleTracker {
  private readonly router = inject(Router);
  private readonly last = signal<IssueModule | null>(null);

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const url = e.urlAfterRedirects;
        if (url.startsWith('/app/support')) return;
        const module = moduleFromRoute(url);
        if (module) this.last.set(module);
      });
  }

  /** The module the user was on before they came to Support, if any. */
  lastModule(): IssueModule | null {
    return this.last();
  }
}
